import GIS from "./app"
import {transform} from "ol/proj";
import {containsCoordinate} from "ol/extent";
import {EnableOptions, IdCallback, OfflineBundle, OfflineSource} from "./configuration/offline";
import TileLayer from "ol/layer/Tile";

class Offline {
    private readonly GIS: GIS;

    private activeId: string | undefined;
    private stayOffline = false;
    private activeIdCallback: IdCallback | undefined;
    private onlineSources: {[key:string]: OfflineSource} = {};
    private bundles: OfflineBundle[] = [];

    constructor(GIS: GIS) {
        this.GIS = GIS;
        this.createEvent = this.createEvent.bind(this);
    }
    /**
     * Initialization of the Offline object.
     * It can be initialized anytime after the whole map window is ready.
     * @param config - Offline bundle configurations.
     */
    public init(config: OfflineBundle[]) {
        this.bundles = config;
    }

    private refreshOnlineSources() {
        for (const layerName in this.onlineSources) {
            if (this.onlineSources.hasOwnProperty(layerName)) {
                const layer = this.GIS.Layer.getLayer(layerName);
                const onlineSource = this.onlineSources[layerName]['source'];
                const onlineExtent = this.onlineSources[layerName]['extent'];
                layer.setSource(onlineSource);
                layer.setExtent(onlineExtent);
            }
        }
    }

    private rollbackSources() {
        this.refreshOnlineSources();

        this.activeId = undefined;
        if (this.activeIdCallback) {
            this.activeIdCallback(null);
        }
        this.onlineSources = {};
    }

    public remove(id: string) {
        const removeIndex = this.bundles.map(item => item.id).indexOf(id);

        (removeIndex >= 0) && this.bundles.splice(removeIndex, 1);
    }

    public activateBundle(bundle: OfflineBundle) {
        if (this.activeId !== bundle.id) {

            this.activeId = bundle.id;

            bundle.layers.map((layerConfig) => {
                const layer = this.GIS.Layer.getLayer(layerConfig.name);

                if (!(layer instanceof TileLayer)) {
                    console.error(`Layer ${layerConfig.name} is not a TileLayer and cannot accept an XYZ source.`);
                    return;
                }

                const source = this.GIS.Layer.createRasterSource(layerConfig);

                this.onlineSources[layerConfig.name] = {
                    source: layer.getSource(),
                    extent: layer.getExtent()
                };
                layer.setSource(source);
                this.GIS.Layer.setLayerExtent(layerConfig.name, bundle.extent, {epsg: bundle.epsg})
            })

            if (this.activeIdCallback) {
                this.activeIdCallback(this.activeId);
            }
        }
    }

    /**
     * Set callback, which fires bundle id when the map window center is located in some offline bundle.
     * When more bundles are overlapping, priority has the last one in the configuration array.
     * Callback can be deactivated when you provide null value for callback parameter
     * @param callback - Callback with degrees and radians, when 'rotationDegreesAndRadians' specified.
     */
    public setActiveIdCallback (callback:IdCallback | undefined) {
        this.activeIdCallback = callback;
    }

    private createEvent() {
        const center = this.GIS.View.getCenter();
        const sourceProjection = this.GIS.View.olObject.getProjection();
        let inside = false;
        const containedBundles: OfflineBundle[] = [];
        this.bundles.map(bundle => {
            const destinationProjection = this.GIS.Projection.getRegistered(bundle.epsg);
            const transformedCenter = transform(center, sourceProjection, destinationProjection);
            if (containsCoordinate(bundle.extent, transformedCenter)) {
                inside = true;
                containedBundles.push(bundle);
            }
        })

        const alreadyActiveBundleInTheExtent = containedBundles.filter(b => b.id === this.activeId).length;
        const numberOfContainedBundles = containedBundles.length;

        if ((numberOfContainedBundles > 1 && alreadyActiveBundleInTheExtent === 0) || numberOfContainedBundles === 1) {
            this.activateBundle(containedBundles[numberOfContainedBundles - 1]);
        }

        if (!inside && this.activeId !== null && !this.stayOffline) {
            this.rollbackSources();
        }
    }
    /**
     * Enable offline maps either in hybrid mode or with option to stay offline completely.
     * @param options
     */
    public enable(options?: EnableOptions) {
        if (options && options.stayOffline) {
            this.stayOffline = options.stayOffline;
        }
        this.GIS.View.olObject.on('change:center', this.createEvent)
    }
    /**
     * Disable offline functionality
     */
    public disable() {
        this.activeIdCallback = undefined;

        this.rollbackSources();

        this.GIS.View.olObject.un('change:center', this.createEvent)

        this.stayOffline = false;
    }
}

export default Offline;